import { useEffect, useRef } from 'react';

import { useI18n } from '../i18n/index.js';
import styles from './AutoLoad.module.css';

interface AutoLoadProps {
  /** 다음 쪽을 불러온다. 끝이면 이 컴포넌트를 아예 안 그린다 */
  onLoad: () => void;
  loading: boolean;
  className?: string | undefined;
}

/**
 * 목록 끝에 두면 **스크롤이 닿을 때 다음 쪽을 부른다.**
 *
 * 버튼을 누르게 하지 않는 이유: 타임라인은 위에서 아래로 훑어 내려가는
 * 화면이라, 중간에 멈춰 버튼을 찾는 동작이 읽는 흐름을 끊는다.
 *
 * ## 두 가지 함정
 *
 * 1. **`rootMargin`으로 미리 부른다.** 바닥에 닿고 나서 부르면 빈 화면을
 *    잠깐 보게 된다. 한 화면 앞에서 미리 부르면 이어 붙는 것처럼 보인다.
 *
 * 2. **다 불러온 뒤에도 자리가 그대로 보이면 다시 불러야 한다.** `IntersectionObserver`는
 *    "보이는 상태가 **바뀔 때**"만 알려준다. 새로 붙은 내용이 화면을 채우지
 *    못하면 이 자리는 계속 보이는 채로 있고, 그러면 두 번째 쪽이 영영 안 온다.
 *    그래서 `loading`이 끝날 때마다 관찰을 다시 건다 — 다시 걸면 지금 상태로
 *    한 번 더 알려준다.
 */
export function AutoLoad({ onLoad, loading, className }: AutoLoadProps) {
  const { t } = useI18n();
  const mark = useRef<HTMLDivElement | null>(null);
  /* 콜백은 렌더마다 새로 온다. 관찰을 그때마다 다시 걸지 않으려고 담아 둔다 */
  const latest = useRef(onLoad);
  latest.current = onLoad;

  useEffect(() => {
    const target = mark.current;
    if (target === null || loading) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          latest.current();
        }
      },
      // 한 화면 앞에서 미리 — 바닥에 닿고 부르면 빈 화면을 잠깐 본다
      { rootMargin: '600px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loading]);

  return (
    <div
      ref={mark}
      className={[styles.mark, className].filter(Boolean).join(' ')}
      role="status"
      aria-live="polite"
    >
      {loading ? <span className={styles.dots}>{t('common.loading')}</span> : null}
    </div>
  );
}
